// District-wide intersection priority list (GET /api/safety/intersections).
// Not corridor-scoped: clusters every junction crash in the state's snapshot
// into physical intersections and ranks them by comprehensive crash cost
// (the state's table — MassDOT 2024 for Massachusetts), by MassDOT's tempered
// HSIP EPDO points, or by raw crash count. Row click flies the map when a
// corridor map is open.
import React, { useEffect, useState } from 'react';
import * as api from './safetyApi';
import { fmtUsd } from './insightsText';

const DISTRICTS = ['', '1', '2', '3', '4', '5', '6'];
const RANKS = [
  { key: 'cost', label: 'Comprehensive cost' },
  { key: 'epdo', label: 'HSIP EPDO points' },
  { key: 'crashes', label: 'Crash count' },
];

export default function IntersectionsPanel({ stateId, onFocus }) {
  const [district, setDistrict] = useState('1');
  const [rank, setRank] = useState('cost');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!stateId) return;
    setLoading(true);
    setError(null);
    api.fetchIntersections(stateId, district, rank)
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [stateId, district, rank]);

  if (!stateId) return null;

  return (
    <div className="safety-insights">
      <div className="safety-insights-nav">
        <label className="safety-filter">
          <span>District</span>
          <select value={district} onChange={(e) => setDistrict(e.target.value)}>
            {DISTRICTS.map((d) => (
              <option key={d} value={d}>{d ? `District ${d}` : 'Statewide'}</option>
            ))}
          </select>
        </label>
        <label className="safety-filter">
          <span>Rank by</span>
          <select value={rank} onChange={(e) => setRank(e.target.value)}>
            {RANKS.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
          </select>
        </label>
        {data && (
          <span className="safety-muted">
            {data.intersectionCount} intersections from {data.junctionCrashes} junction
            crashes · dataset {data.datasetVersion}
          </span>
        )}
      </div>

      {loading && <div className="safety-muted">Clustering intersections… (first load sweeps the statewide snapshot)</div>}
      {error && <div className="safety-error">Intersections unavailable: {error}</div>}

      {data && !loading && (
        <>
          <div className="safety-table-scroll">
            <table className="safety-table safety-table-clickable">
              <thead>
                <tr>
                  <th>#</th><th>Cost</th><th>EPDO</th><th>Crashes</th>
                  <th>K/A/B/C/O</th><th>Major road</th><th>Secondary road</th>
                  <th>City / Town</th><th>Lat, Lon</th><th>Junction</th>
                </tr>
              </thead>
              <tbody>
                {data.intersections.map((r) => (
                  <tr key={r.rank}
                    className={r.mix.K ? 'safety-row-hit' : ''}
                    onClick={() => onFocus && onFocus({
                      lat: r.lat, lng: r.lng, zoom: 16,
                      key: `ix-${r.rank}-${Date.now()}`,
                    })}
                    title="Show on map (when a corridor map is open)">
                    <td>{r.rank}</td>
                    <td><strong>{fmtUsd(r.cost)}</strong></td>
                    <td>{r.epdo}</td>
                    <td>{r.crashes}</td>
                    <td>{`${r.mix.K}/${r.mix.A}/${r.mix.B}/${r.mix.C}/${r.mix.O}`}</td>
                    <td>{r.major}</td>
                    <td>{r.secondary || '?'}</td>
                    <td>{(r.city || '').replace(/\w\S*/g, (w) => w[0] + w.slice(1).toLowerCase())}</td>
                    <td>{r.lat.toFixed(5)}, {r.lng.toFixed(5)}</td>
                    <td>{r.junction}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="safety-muted safety-insight-source">
            Cost basis: {data.costTable?.source}. EPDO uses MassDOT's tempered HSIP
            weights — every fatal-or-injury crash (K/A/B/C) counts {data.epdoWeights?.K} points
            and a property-damage-only crash counts {data.epdoWeights?.O}, deliberately NOT
            proportional to crash costs so rankings don't chase individual fatal crashes.
            Rows with a fatality are highlighted; on the cost ranking one fatal crash
            outweighs dozens of injury crashes — compare with the EPDO ranking.
          </p>
        </>
      )}
    </div>
  );
}
